// src/services/cache.ts

/**
 * Upstash Redis caching service for API data
 * Provides robust caching with TTL and data validation
 * Uses direct fetch calls to avoid browser compatibility issues
 */

// Redis configuration
interface RedisConfig {
  url: string;
  token: string;
}

let redisConfig: RedisConfig | null = null;

// Initialize Redis configuration
try {
  const redisUrl = process.env.REACT_APP_UPSTASH_REDIS_REST_URL;
  const redisToken = process.env.REACT_APP_UPSTASH_REDIS_REST_TOKEN;
  
  if (redisUrl && redisToken) {
    redisConfig = { url: redisUrl, token: redisToken };
    console.log('✅ Upstash Redis configuration initialized');
  } else {
    console.warn('⚠️  Upstash Redis credentials not found - caching disabled');
  }
} catch (error) {
  console.warn('⚠️  Failed to initialize Redis configuration - caching disabled:', error);
}

// Cache configuration
const CACHE_CONFIG = {
  TTL: 30 * 60, // 30 minutes in seconds for fresh data
  FALLBACK_TTL: 48 * 60 * 60, // 48 hours in seconds for fallback data
  VALIDATOR_QUEUE_TTL: 24 * 60 * 60, // 24 hours for validator queue (once per day)
  KEYS: {
    MARKET_CAP: 'crypto:market_cap',
    ETH_RSI: 'crypto:eth_rsi',
    FEAR_GREED: 'crypto:fear_greed',
    BEACONCHAIN: 'crypto:beaconchain',
    VALIDATOR_QUEUE: 'crypto:validator_queue', // New key for validatorqueue.com data
    OPEN_INTEREST: 'crypto:open_interest',
    NEWS_DATA: 'crypto:news',
    WALLET_DATA: 'crypto:wallet_data',
    GOOGLE_TRENDS: 'crypto:google_trends',
  }
} as const;

// Cached data interface
interface CachedData<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

/**
 * Data validation helper to ensure we don't cache invalid data
 * Enhanced with price data validation
 */
const isValidData = <T>(data: T, key?: string): boolean => {
  if (data === null || data === undefined) return false;
  if (typeof data === 'object' && Object.keys(data).length === 0) return false;
  if (Array.isArray(data) && data.length === 0) return false;
  
  // Special validation for price data
  if (key && key.includes('price') && typeof data === 'number') {
    // Reject obviously invalid prices (too low or too high)
    if (data <= 0 || data < 0.0001 || data > 100000) {
      console.warn(`Invalid price data not cached: ${data} for key: ${key}`);
      return false;
    }
  }
  
  // Special validation for SPX6900 price specifically
  if (key === 'spx6900_price' && typeof data === 'number') {
    // SPX6900 should be between $0.01 and $1000 to be realistic
    if (data < 0.01 || data > 1000) {
      console.warn(`Invalid SPX6900 price not cached: $${data}`);
      return false;
    }
  }
  
  return true;
};

/**
 * Check if cached data is fresh (within TTL)
 */
const isFresh = (cachedData: CachedData<any>): boolean => {
  const now = Date.now();
  const age = now - cachedData.timestamp;
  const maxAge = cachedData.ttl * 1000; // Convert seconds to milliseconds
  return age < maxAge;
};

/**
 * Get data from cache using direct fetch
 */
export const getCachedData = async <T>(key: string): Promise<T | null> => {
  if (!redisConfig) {
    return null; // Caching disabled
  }
  
  try {
    const response = await fetch(`${redisConfig.url}/get/${key}`, {
      headers: {
        'Authorization': `Bearer ${redisConfig.token}`
      }
    });
    
    if (!response.ok) {
      console.log(`Cache miss for key: ${key}`);
      return null;
    }
    
    const result = await response.json();
    if (!result.result) {
      console.log(`Cache miss for key: ${key}`);
      return null;
    }
    
    const cached: CachedData<T> = JSON.parse(result.result);
    
    if (!isFresh(cached)) {
      console.log(`Cache expired for key: ${key}`);
      // Don't delete expired data immediately - keep as fallback
      return null;
    }
    
    console.log(`Cache hit for key: ${key}`);
    return cached.data;
  } catch (error) {
    console.error(`Cache read error for key ${key}:`, error);
    return null;
  }
};

/**
 * Set data in cache with dual-tier fallback strategy
 */
export const setCachedData = async <T>(
  key: string, 
  data: T, 
  customTTL?: number
): Promise<boolean> => {
  if (!redisConfig) {
    return false; // Caching disabled
  }
  
  try {
    // Validate data before caching
    if (!isValidData(data, key)) {
      console.warn(`Invalid data not cached for key: ${key}`);
      return false;
    }
    
    const cachedData: CachedData<T> = {
      data,
      timestamp: Date.now(),
      ttl: customTTL || CACHE_CONFIG.TTL
    };
    
    // Set fresh cache with short TTL
    const ttlSeconds = customTTL || CACHE_CONFIG.TTL;
    const freshResponse = await fetch(`${redisConfig.url}/setex/${key}/${ttlSeconds}/${encodeURIComponent(JSON.stringify(cachedData))}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${redisConfig.token}`
      }
    });
    
    // Also set fallback cache with long TTL
    const fallbackKey = `${key}:fallback`;
    const fallbackData: CachedData<T> = {
      data,
      timestamp: Date.now(),
      ttl: CACHE_CONFIG.FALLBACK_TTL
    };
    
    const fallbackResponse = await fetch(`${redisConfig.url}/setex/${fallbackKey}/${CACHE_CONFIG.FALLBACK_TTL}/${encodeURIComponent(JSON.stringify(fallbackData))}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${redisConfig.token}`
      }
    });
    
    if (freshResponse.ok) {
      console.log(`Data cached for key: ${key} (fresh: ${ttlSeconds}s, fallback: ${CACHE_CONFIG.FALLBACK_TTL}s)`);
      return true;
    } else {
      console.error(`Cache write failed for key ${key}:`, freshResponse.status);
      return false;
    }
  } catch (error) {
    console.error(`Cache write error for key ${key}:`, error);
    return false;
  }
};

/**
 * Delete cached data from Redis
 */
export const deleteCachedData = async (key: string): Promise<boolean> => {
  if (!redisConfig) {
    return false; // Caching disabled
  }

  try {
    // Delete both fresh and fallback cache using POST method (Upstash REST API)
    const deleteRequests = [
      fetch(`${redisConfig.url}/del/${key}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${redisConfig.token}`
        }
      }),
      fetch(`${redisConfig.url}/del/${key}:fallback`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${redisConfig.token}`
        }
      })
    ];

    const responses = await Promise.all(deleteRequests);
    const success = responses.every(r => r.ok);

    if (success) {
      console.log(`✅ Cache cleared for key: ${key}`);
    } else {
      console.warn(`⚠️ Failed to clear cache for key: ${key}`);
    }

    return success;
  } catch (error) {
    console.error(`❌ Cache delete error for key ${key}:`, error);
    return false;
  }
};

/**
 * Get fallback data from long-lived cache when fresh data is unavailable
 */
export const getStaleData = async <T>(key: string): Promise<T | null> => {
  if (!redisConfig) {
    return null; // Caching disabled
  }
  
  try {
    // Try fallback cache first (long-lived)
    const fallbackKey = `${key}:fallback`;
    const fallbackResponse = await fetch(`${redisConfig.url}/get/${fallbackKey}`, {
      headers: {
        'Authorization': `Bearer ${redisConfig.token}`
      }
    });
    
    if (fallbackResponse.ok) {
      const fallbackResult = await fallbackResponse.json();
      if (fallbackResult.result) {
        const fallbackCached: CachedData<T> = JSON.parse(fallbackResult.result);
        console.log(`Using fallback data for key: ${key} (age: ${Math.round((Date.now() - fallbackCached.timestamp) / 1000 / 60)} minutes)`);
        return fallbackCached.data;
      }
    }
    
    // If no fallback cache, try old fresh cache (might be expired but still exist)
    const response = await fetch(`${redisConfig.url}/get/${key}`, {
      headers: {
        'Authorization': `Bearer ${redisConfig.token}`
      }
    });
    
    if (!response.ok || !response) {
      return null;
    }
    
    const result = await response.json();
    if (!result.result) {
      return null;
    }
    
    const cached: CachedData<T> = JSON.parse(result.result);
    console.log(`Using expired cache data for key: ${key} (age: ${Math.round((Date.now() - cached.timestamp) / 1000 / 60)} minutes)`);
    return cached.data;
  } catch (error) {
    console.error(`Fallback data read error for key ${key}:`, error);
    return null;
  }
};

/**
 * Cache-first data fetcher with fallback strategy
 */
export const withCache = async <T>(
  key: string,
  fetchFunction: () => Promise<T>,
  customTTL?: number
): Promise<T | null> => {
  try {
    // If Redis is not available, just fetch data directly
    if (!redisConfig) {
      console.log(`Cache disabled - fetching data directly for key: ${key}`);
      return await fetchFunction();
    }
    
    // Try to get fresh cached data first
    const cachedData = await getCachedData<T>(key);
    if (cachedData) {
      return cachedData;
    }
    
    // Cache miss or expired - fetch fresh data
    console.log(`Fetching fresh data for key: ${key}`);
    const freshData = await fetchFunction();
    
    // Validate and cache the fresh data
    if (isValidData(freshData, key)) {
      await setCachedData(key, freshData, customTTL);
      return freshData;
    } else {
      console.warn(`Fresh data invalid for key: ${key}, trying stale data`);
      // If fresh data is invalid, try stale data as fallback
      return await getStaleData<T>(key);
    }
  } catch (error) {
    console.error(`Fetch error for key ${key}:`, error);
    
    // On fetch error, try to return stale data as fallback
    const staleData = await getStaleData<T>(key);
    if (staleData) {
      console.log(`Returning stale data as fallback for key: ${key}`);
      return staleData;
    }
    
    return null;
  }
};

/**
 * Clear invalid cached price data (one-time cleanup)
 */
export const clearInvalidPriceData = async (): Promise<void> => {
  if (!redisConfig) {
    console.log('Cache disabled - no cleanup needed');
    return;
  }
  
  try {
    // Check SPX6900 price specifically
    const spx6900Price = await getCachedData<number>('spx6900_price');
    if (spx6900Price && (spx6900Price < 0.01 || spx6900Price > 1000)) {
      console.log(`🧹 Clearing invalid SPX6900 price: $${spx6900Price}`);
      await clearCache('spx6900_price');
    }
    
    // You can add other price data checks here if needed
    console.log('✅ Price data cleanup completed');
  } catch (error) {
    console.error('❌ Error during price data cleanup:', error);
  }
};

/**
 * Clear cache for a specific key
 */
export const clearCache = async (key: string): Promise<boolean> => {
  if (!redisConfig) {
    return false; // Caching disabled
  }
  
  try {
    const response = await fetch(`${redisConfig.url}/del/${key}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${redisConfig.token}`
      }
    });
    
    if (response.ok) {
      console.log(`Cache cleared for key: ${key}`);
      return true;
    } else {
      console.error(`Cache clear failed for key ${key}:`, response.status);
      return false;
    }
  } catch (error) {
    console.error(`Cache clear error for key ${key}:`, error);
    return false;
  }
};

/**
 * Clear whale analytics cache (for decimal fix)
 */
export const clearWhaleAnalyticsCache = async (): Promise<boolean> => {
  if (!redisConfig) {
    return false; // Caching disabled
  }
  
  try {
    console.log('🧹 Clearing whale analytics cache...');
    
    // Clear whale balance and activity caches
    const whaleKeys = [
      'whale_balance:*',
      'activity:ethereum:*',
      'activity:base:*', 
      'activity:solana:*',
      'eth_balance:*',
      'eth_transfers:*'
    ];
    
    let clearedCount = 0;
    
    // Use Redis SCAN to find and delete pattern-matched keys
    for (const pattern of whaleKeys) {
      try {
        // Get all keys matching pattern (this is a simplified approach)
        // In a real implementation, you'd use SCAN for large datasets
        const response = await fetch(`${redisConfig.url}/keys/${pattern}`, {
          headers: {
            'Authorization': `Bearer ${redisConfig.token}`
          }
        });
        
        if (response.ok) {
          const result = await response.json();
          if (result.result && Array.isArray(result.result)) {
            for (const key of result.result) {
              await clearCache(key);
              clearedCount++;
            }
          }
        }
      } catch (error) {
        console.warn(`Failed to clear pattern ${pattern}:`, error);
      }
    }
    
    console.log(`🧹 Cleared ${clearedCount} whale analytics cache entries`);
    return true;
    
  } catch (error) {
    console.error('Clear whale analytics cache error:', error);
    return false;
  }
};

/**
 * Clear all application cache
 */
export const clearAllCache = async (): Promise<boolean> => {
  if (!redisConfig) {
    return false; // Caching disabled
  }
  
  try {
    const keys = Object.values(CACHE_CONFIG.KEYS);
    const results = await Promise.all(keys.map(key => clearCache(key)));
    const success = results.every(result => result);
    
    if (success) {
      console.log('All cache cleared');
    } else {
      console.warn('Some cache clearing operations failed');
    }
    return success;
  } catch (error) {
    console.error('Clear all cache error:', error);
    return false;
  }
};

/**
 * Cache wallet balance with extended fallback TTL for critical data
 */
export const cacheWalletBalance = async (
  address: string, 
  network: string, 
  balance: number
): Promise<boolean> => {
  const key = `${network.toLowerCase()}_balance:${address}`;
  
  // Use longer TTL for wallet balances since they're critical
  const WALLET_BALANCE_TTL = 60 * 60; // 1 hour for fresh data
  const WALLET_FALLBACK_TTL = 7 * 24 * 60 * 60; // 7 days for fallback
  
  return setCachedData(key, balance, WALLET_BALANCE_TTL);
};

// ========== Sorted Set Operations for Time-Series Data ==========

/**
 * Add entry to a sorted set (for time-series data like market heat history)
 * Uses timestamp as score for efficient time-range queries
 */
export const zaddToCache = async (
  key: string,
  score: number,
  member: string
): Promise<boolean> => {
  if (!redisConfig) {
    return false;
  }

  try {
    const response = await fetch(`${redisConfig.url}/zadd/${key}/${score}/${encodeURIComponent(member)}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${redisConfig.token}`
      }
    });

    if (response.ok) {
      console.log(`Sorted set entry added to key: ${key}`);
      return true;
    } else {
      console.error(`Sorted set add failed for key ${key}:`, response.status);
      return false;
    }
  } catch (error) {
    console.error(`Sorted set add error for key ${key}:`, error);
    return false;
  }
};

/**
 * Get entries from sorted set by score range (time range)
 */
export const zrangeByScore = async <T>(
  key: string,
  min: number,
  max: number
): Promise<T[]> => {
  if (!redisConfig) {
    return [];
  }

  try {
    const response = await fetch(`${redisConfig.url}/zrangebyscore/${key}/${min}/${max}`, {
      headers: {
        'Authorization': `Bearer ${redisConfig.token}`
      }
    });

    if (!response.ok) {
      console.log(`No sorted set data for key: ${key}`);
      return [];
    }

    const result = await response.json();
    if (!result.result || !Array.isArray(result.result)) {
      return [];
    }

    // Parse each JSON string in the result
    return result.result.map((item: string) => {
      try {
        return JSON.parse(item) as T;
      } catch {
        return null;
      }
    }).filter((item: T | null): item is T => item !== null);
  } catch (error) {
    console.error(`Sorted set range error for key ${key}:`, error);
    return [];
  }
};

/**
 * Remove entries from sorted set by score range (cleanup old entries)
 */
export const zremRangeByScore = async (
  key: string,
  min: number,
  max: number
): Promise<boolean> => {
  if (!redisConfig) {
    return false;
  }

  try {
    const response = await fetch(`${redisConfig.url}/zremrangebyscore/${key}/${min}/${max}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${redisConfig.token}`
      }
    });

    if (response.ok) {
      console.log(`Cleaned old entries from sorted set: ${key}`);
      return true;
    } else {
      console.error(`Sorted set cleanup failed for key ${key}:`, response.status);
      return false;
    }
  } catch (error) {
    console.error(`Sorted set cleanup error for key ${key}:`, error);
    return false;
  }
};

/**
 * Get the latest entry from a sorted set (highest score)
 */
export const zrevrangeWithScores = async <T>(
  key: string,
  start: number = 0,
  stop: number = 0
): Promise<Array<{ member: T; score: number }>> => {
  if (!redisConfig) {
    return [];
  }

  try {
    const response = await fetch(`${redisConfig.url}/zrevrange/${key}/${start}/${stop}/WITHSCORES`, {
      headers: {
        'Authorization': `Bearer ${redisConfig.token}`
      }
    });

    if (!response.ok) {
      return [];
    }

    const result = await response.json();
    if (!result.result || !Array.isArray(result.result)) {
      return [];
    }

    // Result comes as [member1, score1, member2, score2, ...]
    const entries: Array<{ member: T; score: number }> = [];
    for (let i = 0; i < result.result.length; i += 2) {
      try {
        entries.push({
          member: JSON.parse(result.result[i]) as T,
          score: parseFloat(result.result[i + 1])
        });
      } catch {
        // Skip invalid entries
      }
    }

    return entries;
  } catch (error) {
    console.error(`Sorted set revrange error for key ${key}:`, error);
    return [];
  }
};

// Export cache keys for use in API functions
export const CACHE_KEYS = CACHE_CONFIG.KEYS;
export const CACHE_TTL = CACHE_CONFIG.TTL;
export const FALLBACK_TTL = CACHE_CONFIG.FALLBACK_TTL;
export const VALIDATOR_QUEUE_TTL = CACHE_CONFIG.VALIDATOR_QUEUE_TTL;

// Make cache clearing available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).clearWhaleCache = clearWhaleAnalyticsCache;
  (window as any).clearAllAppCache = clearAllCache;
  (window as any).clearSpecificCache = clearCache;
  console.log('🧹 Cache clearing functions available:');
  console.log('  - window.clearWhaleCache() - Clear whale analytics cache');
  console.log('  - window.clearAllAppCache() - Clear all application cache');
  console.log('  - window.clearSpecificCache("key") - Clear specific cache key');
}